import { useState, type FormEvent } from 'react';
import { parseRubToKopecks } from '@shared/types';
import type { CardLimits } from '@shared/ledger';

import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useUpdateCardLimits } from '@/lib/mutations';
import { toMessage } from '@/lib/api';

/** Daily and concurrent limits; per-payment amount is always unlimited. */

interface Errors {
  daily?: string;
  perPayment?: string;
  concurrent?: string;
}

/** Whole roubles stay whole: «100000» is easier to edit than «100 000,00». */
function toEditable(kopecks: number): string {
  return kopecks % 100 === 0 ? String(kopecks / 100) : (kopecks / 100).toFixed(2).replace('.', ',');
}

export function CardLimitsForm({
  cardId,
  limits,
  onDone,
}: {
  cardId: string;
  limits: CardLimits;
  onDone: () => void;
}) {
  const toast = useToast();
  const updateLimits = useUpdateCardLimits();

  const [daily, setDaily] = useState(() => toEditable(limits.dailyLimitRub));
  const [concurrent, setConcurrent] = useState(() => String(limits.concurrentLimit));
  const [errors, setErrors] = useState<Errors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setServerError(null);

    const dailyKopecks = parseRubToKopecks(daily);
    const concurrentCount = Number(concurrent.trim());

    const found: Errors = {};

    if (Number.isNaN(dailyKopecks)) {
      found.daily = 'Введите сумму в рублях или 0 — без ограничения.';
    } else if (dailyKopecks < 0) {
      found.daily = 'Лимит не может быть отрицательным.';
    }


    if (!Number.isInteger(concurrentCount) || concurrentCount < 0 || concurrentCount > 100) {
      found.concurrent = 'Введите целое число от 0 до 100. 0 — без ограничения.';
    }

    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      await updateLimits.mutateAsync({
        cardId,
        limits: {
          dailyLimitRub: dailyKopecks,
          perPaymentLimitRub: 0,
          concurrentLimit: concurrentCount,
        },
      });
      toast.success('Лимиты сохранены');
      onDone();
    } catch (error) {
      setServerError(toMessage(error, 'Не удалось сохранить лимиты.'));
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate className="mt-3">
      {serverError ? <InlineError className="mb-3" message={serverError} /> : null}

      <TextField
        label="Лимит за сутки, ₽"
        value={daily}
        onChange={(event) => setDaily(event.target.value)}
        error={errors.daily}
        hint="Скользящие 24 часа. 0 — без ограничения."
        inputMode="decimal"
        autoComplete="off"
        monospace
      />


      <TextField
        containerClassName="mt-4"
        label="Одновременных платежей"
        value={concurrent}
        onChange={(event) => setConcurrent(event.target.value)}
        error={errors.concurrent}
        hint="0 — без ограничения числа одновременных платежей."
        inputMode="numeric"
        autoComplete="off"
        monospace
      />

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button type="submit" variant="primary" isLoading={updateLimits.isPending}>
          Сохранить лимиты
        </Button>
        <Button type="button" onClick={onDone} disabled={updateLimits.isPending}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
